import { useCompositionStore } from "../../stores/compositionStore.js";
import { assetUrl } from "../../infra/api.js";
import type { JobUiState } from "../../stores/compositionStore.js";
import type { SectionVariation } from "../../domain/index.js";
import { Badge } from "../../ui/Badge.js";
import { Button } from "../../ui/Button.js";

interface SectionVariationsProps {
  sectionId: string;
  /**
   * Read-only when the editor is in history time-travel mode (Phase 8): the
   * "Promover" button is suppressed because promoting appends an event.
   */
  readOnly?: boolean;
  /**
   * When set, the panel renders THESE variations instead of the live store's —
   * used in time-travel mode so the A/B panel shows the past state's
   * candidates. `undefined` => read the live `section.variations`.
   */
  variationsOverride?: SectionVariation[];
}

const EMPTY_VARIATIONS: SectionVariation[] = [];

/** Formats a clip length in `m:ss` from milliseconds. */
function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/**
 * Counts `section-variation` jobs still in flight FOR THIS SECTION. Each batch
 * enqueues one job per candidate; until the worker emits
 * `SectionVariationsGenerated` the candidate has no card yet — this is what the
 * "gerando" rows stand in for. Scoped by `sectionId` so a batch generating for
 * one section does not paint placeholders into every other section's panel.
 */
function pendingVariationJobs(jobs: Record<string, JobUiState>, sectionId: string): JobUiState[] {
  return Object.values(jobs).filter(
    (j) =>
      j.kind === "section-variation" &&
      j.sectionId === sectionId &&
      j.status !== "completed" &&
      j.status !== "failed",
  );
}

export function SectionVariations({
  sectionId,
  readOnly = false,
  variationsOverride,
}: SectionVariationsProps): JSX.Element {
  const liveVariations = useCompositionStore(
    (s) =>
      s.audioState.sections.find((sec) => sec.id === sectionId)?.variations ?? EMPTY_VARIATIONS,
  );
  const variations = variationsOverride ?? liveVariations;
  const projectId = useCompositionStore((s) => s.projectId);
  const jobs = useCompositionStore((s) => s.jobs);
  const promoteStatus = useCompositionStore((s) => s.variations.status[sectionId]);
  const promoteError = useCompositionStore((s) => s.variations.error[sectionId]);

  // In time-travel mode the panel shows a past snapshot — in-flight jobs of the
  // live present are irrelevant, so suppress the "gerando" placeholders.
  const pending = variationsOverride !== undefined ? [] : pendingVariationJobs(jobs, sectionId);
  const promoting = promoteStatus === "promoting";

  function onPromote(variationId: string): void {
    void useCompositionStore
      .getState()
      .promoteVariation(sectionId, variationId)
      .catch(() => {
        // error is recorded in `variations.error[sectionId]`
      });
  }

  // Group candidates by `batchId` — each "Gerar variações" click is one batch.
  // Newest batch on top: batches arrive in log order, so reverse the first-seen
  // order of the batchIds.
  const batchOrder: string[] = [];
  const byBatch = new Map<string, SectionVariation[]>();
  for (const v of variations) {
    if (!byBatch.has(v.batchId)) {
      byBatch.set(v.batchId, []);
      batchOrder.push(v.batchId);
    }
    byBatch.get(v.batchId)!.push(v);
  }
  const orderedBatches = [...batchOrder].reverse();

  // Empty state: no candidates and no batch in flight.
  if (variations.length === 0 && pending.length === 0) {
    return (
      <div
        className="flex flex-col gap-1 border border-dashed border-border bg-asphalt/40 px-4 py-5"
        aria-label={`Variations ${sectionId}`}
      >
        <span className="font-mono text-[11px] uppercase tracking-widest2 text-paper">
          Sem variações
        </span>
        <p className="max-w-md text-xs leading-relaxed text-ink-muted">
          Gere takes alternativos desta seção e compare-os lado a lado — promova o que você
          preferir; os outros continuam acessíveis.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" aria-label={`Variations ${sectionId}`}>
      {promoteStatus === "error" && promoteError ? (
        <span role="alert" className="text-xs text-accent-danger">
          {promoteError}
        </span>
      ) : null}

      {orderedBatches.map((batchId, batchIdx) => {
        const items = byBatch.get(batchId) ?? [];
        return (
          <section key={batchId} className="flex flex-col gap-2" aria-label={`Batch ${batchId}`}>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-widest2 text-ink-dim">
                {batchIdx === 0 ? "Lote mais recente" : `Lote ${orderedBatches.length - batchIdx}`}
              </span>
              <span aria-hidden="true" className="h-px flex-1 bg-border" />
              <span className="font-mono text-[10px] tabular-nums text-ink-dim">
                {items.length} {items.length === 1 ? "take" : "takes"}
              </span>
            </div>
            <ul
              className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3"
              aria-label="Variation candidates"
            >
              {items.map((variation) => (
                <li key={variation.variationId}>
                  <article
                    aria-label={`Variation ${variation.label}`}
                    className={`flex h-full flex-col gap-3 border bg-tapeElev p-3 transition-colors ${
                      variation.promoted ? "border-butter shadow-glow" : "border-border"
                    }`}
                  >
                    <header className="flex items-center justify-between gap-2">
                      <span className="truncate font-mono text-[11px] uppercase tracking-widest2 text-paper">
                        {variation.label}
                      </span>
                      {variation.promoted ? (
                        <Badge tone="active" aria-label="Variação ativa">
                          ativa
                        </Badge>
                      ) : (
                        <Badge tone="neutral" aria-label={`Duração ${variation.durationMs}ms`}>
                          {formatDuration(variation.durationMs)}
                        </Badge>
                      )}
                    </header>

                    {/* Mini-player: a plain <audio> pointed at the variation's
                        content-addressed asset (plan Task 5.4, option b). The
                        whole-song PlaybackEngine is untouched — A/B preview is
                        an isolated, single-clip listen. */}
                    {projectId ? (
                      <audio
                        controls
                        preload="none"
                        aria-label={`Preview ${variation.label}`}
                        src={assetUrl(projectId, variation.assetHash)}
                        className="w-full"
                      />
                    ) : null}

                    <Button
                      variant={variation.promoted ? "secondary" : "primary"}
                      aria-label={`Promover ${variation.label}`}
                      disabled={readOnly || variation.promoted || promoting || !projectId}
                      onClick={() => {
                        onPromote(variation.variationId);
                      }}
                    >
                      {variation.promoted ? "Take ativo" : promoting ? "Promovendo…" : "Promover"}
                    </Button>
                  </article>
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      {pending.length > 0 ? (
        <ul
          className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3"
          aria-label="Generating variations"
        >
          {pending.map((job) => (
            <li key={job.id}>
              <article
                aria-label="Variation generating"
                className="flex h-full flex-col gap-3 border border-dashed border-butter/50 bg-tapeElev/60 p-3"
              >
                <header className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[11px] uppercase tracking-widest2 text-butter">
                    gerando…
                  </span>
                  <span className="font-mono text-[10px] tabular-nums text-ink-dim">
                    {job.percent !== undefined ? `${Math.round(job.percent)}%` : "—"}
                  </span>
                </header>
                <div
                  className="h-1 w-full bg-tapeHi"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={job.percent !== undefined ? Math.round(job.percent) : 0}
                  aria-label="Variation generation progress"
                >
                  <div
                    className="h-full bg-butter transition-all"
                    style={{ width: `${job.percent ?? 4}%` }}
                  />
                </div>
                <p className="text-xs leading-relaxed text-ink-muted">
                  Um novo take está sendo renderizado pela IA.
                </p>
              </article>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
